import React from 'react';
import { AlertCircle, RefreshCw } from 'lucide-react';

export interface ErrorBoundaryProps {
  children: React.ReactNode;
  fallback?: React.ReactNode;
  compact?: boolean;
}

export interface ErrorBoundaryState {
  hasError: boolean;
  error: Error | null;
}

export class ErrorBoundary extends React.Component<ErrorBoundaryProps, ErrorBoundaryState> {
  constructor(props: ErrorBoundaryProps) {
    super(props);
    this.state = { hasError: false, error: null };
  }

  static getDerivedStateFromError(error: Error): ErrorBoundaryState {
    return { hasError: true, error };
  }

  componentDidCatch(error: Error, errorInfo: React.ErrorInfo) {
    console.error('ErrorBoundary caught:', error, errorInfo);
    if (
      typeof window !== 'undefined' &&
      error?.message &&
      (error.message.includes('dynamically imported module') ||
        error.message.includes('Importing a module script failed'))
    ) {
      const key = 'last_chunk_reload';
      const now = Date.now();
      const lastReload = parseInt(sessionStorage.getItem(key) || '0', 10);
      if (now - lastReload > 10000) {
        sessionStorage.setItem(key, String(now));
        window.location.reload();
      }
    }
  }

  render() {
    if (this.state.hasError) {
      if (this.props.fallback) {
        return this.props.fallback;
      }

      if (this.props.compact) {
        return (
          <div className="flex items-center justify-between gap-3 rounded-xl border border-destructive/30 bg-destructive-soft p-3 text-xs text-destructive animate-fade-in shadow-xs">
            <div className="flex items-center gap-2 min-w-0">
              <AlertCircle className="h-4 w-4 shrink-0" />
              <div className="min-w-0">
                <p className="font-semibold truncate">Something went wrong</p>
                <p className="text-[11px] opacity-90 truncate">
                  {this.state.error?.message || 'Component failed to render'}
                </p>
              </div>
            </div>
            <button
              type="button"
              onClick={() => this.setState({ hasError: false, error: null })}
              className="inline-flex items-center gap-1.5 shrink-0 rounded-lg bg-destructive px-2.5 py-1 text-[11px] font-semibold text-white hover:bg-destructive/90 transition-colors shadow-xs"
            >
              <RefreshCw className="h-3 w-3" />
              Try Again
            </button>
          </div>
        );
      }

      return (
        <div className="flex min-h-[360px] w-full items-center justify-center p-6 bg-background">
          <div className="w-full max-w-md space-y-4 rounded-2xl border border-border/80 bg-surface p-6 shadow-xl text-center backdrop-blur-md">
            <div className="mx-auto grid h-12 w-12 place-items-center rounded-2xl border border-destructive/25 bg-destructive-soft text-destructive">
              <AlertCircle className="h-6 w-6" />
            </div>
            <div className="space-y-1">
              <h1 className="text-xl font-bold tracking-tight text-foreground">Something went wrong</h1>
              <p className="text-xs text-muted-foreground leading-relaxed">
                {this.state.error?.message || 'An unexpected error occurred'}
              </p>
            </div>
            <div className="pt-2">
              <button
                type="button"
                onClick={() => this.setState({ hasError: false, error: null })}
                className="inline-flex items-center justify-center gap-2 rounded-xl bg-primary px-5 py-2.5 text-xs font-bold text-primary-foreground shadow-md hover:bg-primary-hover transition-all active:scale-[0.99]"
              >
                <RefreshCw className="h-3.5 w-3.5" />
                Try Again
              </button>
            </div>
          </div>
        </div>
      );
    }

    return this.props.children;
  }
}

export function ComponentErrorBoundary({ children, fallback }: { children: React.ReactNode; fallback?: React.ReactNode }) {
  return <ErrorBoundary compact fallback={fallback}>{children}</ErrorBoundary>;
}
